import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import { DragDropProvider, DragEndEvent } from '@dnd-kit/react';
import { move } from '@dnd-kit/helpers';

import { Stage as StageItem } from '../../../features/process/components/stage';
import { EmptyStagePlaceholder } from '../../../features/process/components/empty-stage-placeholder';
import { Project, Stage, Step } from '../../../app/entities';
import { StepItem } from '../../../components/step/step-item';
import { catchError } from '../../../core/catch-error';

interface OverViewTabProps {
  project: Project;
  onCreateStage: () => void;
}

export const OverviewTab: React.FC<OverViewTabProps> = observer(({ project, onCreateStage }) => {
  const [items, setItems] = useState<Record<string, string[]>>(() => {
    const stages = project.stages.slice().sort((a, b) => a.order - b.order);

    const obj = {};

    for (const stage of stages) {
      obj[stage._id] = stage.steps.map((step) => step._id);
    }

    return obj;
  });

  useEffect(() => {
    const stages = project.stages.slice().sort((a, b) => a.order - b.order);

    const obj = {};

    for (const stage of stages) {
      obj[stage._id] = stage.steps.map((step) => step._id);
    }

    setItems(obj);
  }, [project.stages, project.steps]);

  const handleDragEnd: DragEndEvent = (event, manager) => {
    const { source, target } = event.operation;

    // if (event.canceled || source.type !== 'column') return;
    // setColumnOrder((columns) => move(columns, event));

    if (source.type === 'item') {
      /**
       * 1. If an item is moved in the same column, only change order
       * 2. If an item is moved in another column, move stage and reorder
       */

      const { initialIndex, index } = source;
      const step = Step.getOne(source.id);

      const hasMovedColumn = step.stageId !== source.group

      /**
       * If the stage is dropped on its position, we cancel the operation
       */
      if (!hasMovedColumn && initialIndex === index) {
        return;
      }

      /**
       * If the source and target group is the same, the step was dragged in the same column
       * Due to optimistic update, the source and the target are always the same
       */
      if (!hasMovedColumn) {
        const targetStage = Stage.getOne(source.group);
        targetStage.updateStepOrder(source.id, index + 1);
      } else {
        step.moveStepToStage(target.group, index + 1);
      }
    } else if (source.type === 'column') {
      /**
       * 1. If a column is moved, reorder stages
       */

      const { initialIndex, index } = source;

      /**
       * If the stage is dropped on its position, we cancel the operation
       */
      if (initialIndex === index) {
        return;
      }

      const draggedStage = Stage.getOne(source.id);
      if (draggedStage) {
        draggedStage.update({ order: index + 1 }).catch(catchError);
      }
    }
  };

  return (
    <Container>
      <DragDropProvider
        onDragOver={(event) => {
          const { source } = event.operation;

          if (source && source.type === 'column') {
            return;
          }

          setItems((items) => move(items, event));
        }}
        onDragEnd={handleDragEnd}
      >
        {Object.keys(items).length === 0 ? (
          <EmptyStagePlaceholder onClick={onCreateStage} />
        ) : (
          Object.keys(items).map((stageId, index) => (
            <StageItem key={stageId} index={index} stageId={stageId}>
              {items[stageId].map((stepId, index) => (
                <StepItem key={stepId} index={index} stepId={stepId} stageId={stageId} />
              ))}
            </StageItem>
          ))
        )}
      </DragDropProvider>
    </Container>
  );
});

const Container = styled.div`
  margin-top: 1.6rem;
  display: flex;
  // justify-content: space-between;
  max-width: 100%;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  height: 100%;
  padding-bottom: 1.6rem;
  gap: 1.6rem;
`;
